// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { defineMessages, injectIntl } from "react-intl";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartEvent,
  ChartOptions,
  LegendItem,
  ActiveElement,
} from "chart.js";

import { Pie } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";

import { ActionType, IAction, IActionSortTable, raiseAction } from "actions";
import { IState } from "states/states";
import { IDropdownSelectOption } from "./DropdownSelect";
import { IActionTypeSelectDashboardItem } from "actions";
import { isSet } from "utils";
import ChartBaseComponent, {
  IChartBaseDispatch,
  IChartBaseExtProps,
  IChartBaseProps,
  mapStateToProps as baseMapStateToProps,
  mapDispatchToProps as baseMapDispatchToProps,
  IChartTariffNode,
} from "./ChartBase";

interface IPowerPieChartExtProps extends IChartBaseExtProps {
  dashboardIndex?: number;
}

interface IPowerPieChartProps extends IChartBaseProps {}

interface IPowerPieChartState {
  redrawToggle: boolean;
}

interface IPowerPieChartDispatch extends IChartBaseDispatch {
  onSort: (sort: IActionSortTable) => void;
  onDropdownTariffSelectionChanged: (index: number, selectedIds: number[]) => void;
}

const mapStateToProps = (state: IState, ownProps: IPowerPieChartExtProps) => {
  const props: IPowerPieChartProps = {
    ...baseMapStateToProps(state, ownProps),
  };
  return props;
};

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IPowerPieChartDispatch = {
    ...baseMapDispatchToProps(dispatch),
    onSort: (sort: IActionSortTable) => {
      dispatch(raiseAction(ActionType.ACT_SORT_TABLE, sort));
    },
    onDropdownTariffSelectionChanged: (index: number, selectedIds: number[]) => {
      const payload: IActionTypeSelectDashboardItem = { index, selectedIds };
      dispatch(raiseAction(ActionType.ACT_SELECT_TARIFFCIRCUITS, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  internalError: {
    id: "internal error...",
  },
  noDataAvailable: {
    id: "No data available.",
  },
});

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

class PowerPieChartComponent extends ChartBaseComponent<
  IPowerPieChartProps & IPowerPieChartExtProps & IPowerPieChartDispatch,
  IPowerPieChartState
> {
  state = {
    showZeroValues: true,
    redrawToggle: false,
  };
  private tlEntries: IChartTariffNode[] = [];

  // a class var which holds a list of all visible TariffCurcuits(option) which is shown in the drop-down menu
  // needs to be a class-var, since the callback of the legend has no information about this option-list!
  private allTariffCircuits: IDropdownSelectOption[] = [];

  // render pie-view
  private renderPieView() {
    const values: number[] = [];
    const labels: string[] = [];
    const backgroundColors: string[] = [];
    const borderColors: string[] = [];

    const selectedItems = this.getSelectedTariffCircuits(
      this.props.selectedEnergySource!,
      true, // show only selected tariffs
      this.props.selectedHierarchyId === undefined, // allow all hierarchies
      false, // ignore categories
      false, // do not show all children
      // do not ignore coloring
    );

    const allItems = this.getSelectedTariffCircuits(
      this.props.selectedEnergySource!,
      false, // show only selected tariffs
      this.props.selectedHierarchyId === undefined, // allow all hierarchies
      false, // ignore categories
      false, // do not show all children
      // do not ignore coloring
    );

    this.tlEntries = allItems.tlEntries;
    const usedtlOptions = selectedItems.tlOptions;

    this.tlEntries.forEach((tariff, curIndex) => {
      values.push(usedtlOptions[curIndex].visible === true ? Math.abs(tariff.value ?? 0) : 0);
      labels.push(`${usedtlOptions[curIndex].label}`);
      backgroundColors.push(`${tariff?.color}40`);
      borderColors.push(`${tariff?.color}`);
    });

    // store in class-instance to make sure the callback of the legend-items can access it!
    this.allTariffCircuits = [...usedtlOptions];

    const data = {
      labels,
      datasets: [
        {
          label: "TariffCurcuit values",
          data: values,
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 1,
        },
      ],
    };

    const options: ChartOptions<"pie"> = {
      animation: false,
      plugins: {
        legend: {
          display: true,
          position: "right" as const,
          onClick: (evt: any, legendItem: any, legend: any) =>
            this.legendItemClick(evt, legendItem, legend),
        },
        tooltip: {
          enabled: true,
          callbacks: {
            label: (tooltipItem) => {
              const tariffCircuit = this.tlEntries[tooltipItem.dataIndex];
              // do we face a background fill data set?
              const valueText = tariffCircuit.valueText;
              return `${tariffCircuit.name}: ${valueText}`;
            },
          },
        },
        datalabels: {
          display: true,
          formatter: (value, context) => {
            // hide labels for zero values
            if (value === 0) return "";

            const tariffCircuit = this.tlEntries[context.dataIndex];
            const valueText = tariffCircuit?.valueText;
            return `${valueText}`;
          },
        },
      },
      onClick: (event, elements, chart) => this.onChartClick(elements),
      onHover: (event, elements, chart) => this.checkMouse(elements),
    };
    // add style definitions of custom emptyPie plugin
    (options.plugins as any).emptyPie = {
      color: "rgba(0, 0, 0, 0.5)",
      width: 2,
      radiusDecrease: 20,
    };

    return (
      <div style={{ width: "600px", margin: "auto" }}>
        <Pie
          data={data}
          options={options}
          plugins={[
            {
              id: "emptyPie",
              beforeDraw: (chart, args, options) => {
                if (isSet(this.allTariffCircuits)) {
                  chart.legend?.legendItems?.forEach((anItem, legendIndex) => {
                    const aTariff = this.allTariffCircuits[legendIndex];
                    anItem.hidden = isSet(aTariff) ? aTariff?.visible === false : true;
                  });
                }
              },
              afterDatasetDraw: (chart, args, options) => {
                const { datasets } = chart.data;
                const { color, width, radiusDecrease } = options;
                let hasData = false;
                let allHidden = true;
                const {
                  chartArea: { left, top, right, bottom },
                  ctx,
                } = chart;

                if (!isSet(ctx)) {
                  // leave if we do not have a valid context to draw to!
                  return;
                }
                // calculate the middle and radius of the circle we want to draw
                const centerX = (left + right) / 2;
                const centerY = (top + bottom) / 2;
                const r = Math.min(right - left, bottom - top) / 1.9;
                let infoText: string | undefined = undefined;

                // check if any dataset is available -> otherwise just draw a text with
                // "internal-error"
                if (datasets !== undefined) {
                  // loop thru all data-sets and check if any of the datasets has valid values
                  // and/or if the dataset is hidden (not selected)
                  for (let i = 0; i < datasets.length; i += 1) {
                    const dataset = datasets[i];
                    for (let i_data = 0; i_data < dataset.data.length; i_data += 1) {
                      allHidden =
                        (allHidden && chart.legend?.legendItems?.[i_data]?.hidden) ?? true;

                      hasData = hasData || (dataset.data[i_data] as number) > 0;
                    }
                  }
                  // if we have no data, or all of the tariffs are hidden (unselected)
                  if (!hasData || allHidden) {
                    // show circle -> prepare error-message
                    infoText = this.props.intl.formatMessage(messages.noDataAvailable);
                  }
                  // otherwise do not draw anything additional
                } else {
                  infoText = this.props.intl.formatMessage(messages.internalError);
                }

                if (isSet(infoText)) {
                  ctx.beginPath();
                  ctx.lineWidth = width || 2;
                  ctx.strokeStyle = color || "rgba(0, 0, 0, 0.5)";
                  ctx.arc(centerX, centerY, r - radiusDecrease || 0, 0, 2 * Math.PI);
                  ctx.font = "30px serif";
                  ctx.fillStyle = color || "rgba(0, 0, 0, 0.5)";
                  ctx.textAlign = "center";
                  ctx.fillText(infoText, centerX, centerY);
                  ctx.stroke();
                }
              },
            },
          ]}
        />
      </div>
    );
  }

  // callback if a legend-item was clicked!
  private legendItemClick(evt: ChartEvent, legendItem: LegendItem, legend: any) {
    const index = legendItem.index;

    let newSelectedTariff: IDropdownSelectOption[] = [];

    // ignore click-callbacks with missing informations
    if (index === undefined) {
      return;
    }

    if ((evt.native as PointerEvent).shiftKey) {
      // with shift-key - select only the one clicked (all others deselected) if it was not selected before
      // otherwise select all again

      // check if any curve is already hidden...
      const filtered = this.allTariffCircuits?.filter((tariff) => tariff.visible === false);
      const anyHidden = filtered ? filtered.length > 0 : 0;

      // check if the clicked curve is hidden AND if any other curve is also hidden
      if (legendItem.hidden === false && anyHidden) {
        // if yes: show all again
        newSelectedTariff =
          this.allTariffCircuits?.map((anItem) => {
            // update entry
            anItem.visible = true;
            return anItem;
          }) ?? [];
      } else {
        // otherwise: hide all except current one.... (leave var empty)
        newSelectedTariff =
          this.allTariffCircuits
            ?.filter((anItem, anIndex) => {
              return anIndex === index;
            })
            .map((anItem) => {
              // update entry
              anItem.visible = Boolean(legendItem.hidden);
              return anItem;
            }) ?? [];
      }
    } else {
      // on NOT shift-click, select/deselct the one clicked
      newSelectedTariff = (
        this.allTariffCircuits?.map((anItem, anIndex) => {
          if (anIndex === index) {
            anItem.visible = Boolean(legendItem.hidden);
          }
          return anItem;
        }) ?? []
      ).filter((item) => item?.visible);
    }

    // store information in gui-state
    if (newSelectedTariff.length > 0) {
      this.props.onDropdownTariffSelectionChanged(
        this.props.selectedDashboardIndex,
        newSelectedTariff.map((curItem) => curItem.id),
      );
    } else {
      this.setState({ redrawToggle: !this.state.redrawToggle });
    }
  }

  // render the component
  public render() {
    return <div id="piechart">{this.renderPieView()}</div>;
  }

  private onChartClick(elements: ActiveElement[]) {
    // tariff clicked -> use as new root
    const hoveredPieIndex = elements.length ? elements[0].index : undefined;
    if (isSet(hoveredPieIndex)) {
      const clickedTariffId = this.tlEntries[hoveredPieIndex].id;
      if (
        isSet(clickedTariffId) &&
        clickedTariffId !== this.props.selectedHierarchyId &&
        this.props.tariffCircuits.some((tariff) => tariff.id === clickedTariffId)
      ) {
        this.props.onHierarchyChanged(this.props.selectedDashboardIndex, clickedTariffId);
      }
    }
  }

  private checkMouse(elements: ActiveElement[]) {
    // tariff hovered -> show different mouse-pointer
    const hoveredPieIndex = elements.length ? elements[0].index : undefined;
    const element = document.getElementById("piechart")?.getElementsByTagName("canvas")[0];

    if (isSet(hoveredPieIndex)) {
      const hoveredTariffId = this.tlEntries[hoveredPieIndex].id;
      const isClickable =
        isSet(hoveredTariffId) &&
        hoveredTariffId !== this.props.selectedHierarchyId &&
        this.props.tariffCircuits.some((tariff) => tariff.id === hoveredTariffId);
      if (isSet(element)) {
        element.style.cursor = isClickable ? "pointer" : "default";
      }
    } else {
      if (isSet(element)) {
        element.style.cursor = "default";
      }
    }
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(PowerPieChartComponent));
